/** Step 1, Equipment: the country, then the module, inverter or microinverter and optimizer from a manufacturer/model
 * typeahead over the catalogue, the CEC lists and the company's own equipment ("Add your own…"), and the licence; the
 * strings or branches are sized live against the inverter's window. Nothing is chosen silently: until the installer
 * picks (or the company has a default), the preview uses a placeholder and the step shows the choice as missing. */
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useOrgId } from "../api/org";
import { Card, Field, Select, TextInput, cx } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { countryChoices, type Country } from "../lib/permits";
import { ROLE_LABEL, chosenId, type EquipmentRole } from "../lib/permitEquipment";
import { CustomEquipment } from "./CustomEquipment";
import { useEquipmentItem } from "./data";
import { useFormat } from "./format";
import { EquipmentPicker } from "./EquipmentPicker";
import { DatasheetList, RsdPicker } from "./Documents";
import { StepChecks, StepHead, useFocusAnchor, type Ws } from "./PermitWorkspace";

/** A number with its unit stays left-to-right inside Arabic or Hebrew text. */
const ltr = (chunks: React.ReactNode[]) => <bdi dir="ltr">{chunks}</bdi>;

export function EquipmentStep({ ws }: { ws: Ws }) {
  useFocusAnchor(ws.focus);
  const t = useT();
  const f = useFormat();
  const orgId = useOrgId();
  const resolved = ws.preview.data?.inputs as Record<string, unknown> | undefined;
  const country = (ws.inputs.country ?? (ws.preview.data?.country as Country | undefined)) || undefined;
  const chosen = (role: EquipmentRole) => chosenId(role, ws.inputs, resolved);
  const inverter = useEquipmentItem(chosen("inverter") || null).data;
  const optimizer = useEquipmentItem(chosen("optimizer") || null).data;
  const needsOptimizer = !!inverter?.specs && !!(inverter.specs as Record<string, unknown>).optimizer_string;
  const [adding, setAdding] = useState<{ role: EquipmentRole; q: string } | null>(null);
  const e = ws.preview.data?.electrical;
  const limit = (e?.limits as Record<string, number> | null | undefined)?.voltage_limit_v;
  const off = !ws.canEdit;
  const set = (role: EquipmentRole, id: string | undefined) =>
    ws.setInputs(role === "inverter" ? { inverter_id: id, optimizer_id: undefined } : role === "module" ? { module_id: id } : { optimizer_id: id });
  const picker = (role: EquipmentRole, label: string, hint?: string) => (
    <div data-anchor={role} className="pw-anchor">
      <Field label={label} hint={hint}>
        <EquipmentPicker role={role} orgId={orgId} country={country} value={chosen(role)} disabled={off} onChange={(id) => set(role, id)} onAddOwn={off ? undefined : (q) => setAdding({ role, q })} />
      </Field>
    </div>
  );

  return (
    <div className="pw-step-body">
      <StepHead title={t("permit.step.equipment")} description={t("permit.equipmentStep.description")} status={ws.statuses.equipment} />
      <div className="pw-cols">
        <Card title={t("permit.equipmentStep.cardTitle")} description={t("permit.equipmentStep.cardDescription")}>
          <div className="pw-form-grid single">
            <Field label={t("permit.equipmentStep.country")}>
              <Select<Country | ""> value={country ?? ""} disabled={off} onChange={(v) => v && ws.setInputs({ country: v, module_id: undefined, inverter_id: undefined, optimizer_id: undefined })} options={countryChoices(t, country)} ariaLabel={t("permit.equipmentStep.country")} />
            </Field>
            {picker("module", t(ROLE_LABEL.module))}
            {picker("inverter", t(ROLE_LABEL.inverter))}
            {needsOptimizer && picker("optimizer", t(ROLE_LABEL.optimizer), t("permit.equipmentStep.optimizerHint"))}
            <RsdPicker ws={ws} inverter={inverter} optimizer={optimizer} />
            <Field label={t("permit.equipmentStep.licence")} hint={t("permit.equipmentStep.licenceHint")}>
              <TextInput value={ws.inputs.licence ?? ""} disabled={off} onChange={(ev) => ws.setInputs({ licence: ev.target.value })} placeholder={((resolved?.contractor as Record<string, string> | undefined)?.licence) ?? ""} maxLength={120} />
            </Field>
          </div>
          <StepChecks ws={ws} step="equipment" />
          {adding && (
            <CustomEquipment
              orgId={orgId}
              role={adding.role}
              initial={adding.q}
              onClose={() => setAdding(null)}
              onCreated={(item) => {
                set(adding.role, item.id);
                setAdding(null);
              }}
            />
          )}
        </Card>

        <Card
          title={e?.system.topology === "micro" ? t("permit.equipmentStep.branchCircuits") : t("permit.equipmentStep.strings")}
          description={
            !e
              ? (ws.preview.error ? t("permit.workspace.preview.failed") : t("permit.equipmentStep.sizing"))
              : e.system.dc_ac_ratio != null
                ? <FormattedMessage id="permit.equipmentStep.summaryWithRatio" values={{ modules: e.system.modules, dc: f.num(e.system.dc_kw, 2), ac: f.num(e.system.ac_kw, 2), ratio: f.num(e.system.dc_ac_ratio, 2), ltr }} />
                : <FormattedMessage id="permit.equipmentStep.summary" values={{ modules: e.system.modules, dc: f.num(e.system.dc_kw, 2), ac: f.num(e.system.ac_kw, 2), ltr }} />
          }
        >
          <div data-anchor="strings" className={cx("pw-anchor", ws.preview.isFetching && "pw-stale")}>
            {!e ? (
              ws.preview.error ? null : (
                <div className="pw-skeleton">
                  <div />
                  <div />
                </div>
              )
            ) : e.system.topology === "micro" ? (
              <table className="pw-table">
                <thead>
                  <tr><th>{t("permit.equipmentStep.col.branch")}</th><th className="num">{t("permit.equipmentStep.col.units")}</th><th className="num">{t("permit.equipmentStep.col.current")}</th><th className="num">{t("permit.equipmentStep.col.breaker")}</th></tr>
                </thead>
                <tbody>
                  {e.branches.map((b) => (
                    <tr key={b.id}><td>{b.id}</td><td className="num">{f.num(b.units)}</td><td className="num">{f.num(b.current_a, 2)} A</td><td className="num">{f.num(b.ocpd_a)} A</td></tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="pw-table">
                <thead>
                  <tr>
                    <th>{t("permit.equipmentStep.col.string")}</th>
                    <th>{t("permit.equipmentStep.col.input")}</th>
                    <th className="num">{t("permit.equipmentStep.col.modules")}</th>
                    <th style={{ width: "40%" }}>
                      {e.system.topology === "optimizer" ? t("permit.equipmentStep.col.stringVoltage") : limit ? t("permit.equipmentStep.col.vocVsLimit", { limit: f.num(limit) }) : t("permit.equipmentStep.col.vocVsNoLimit")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {e.strings.map((s) => {
                    const v = s.voc_cold_v ?? s.vmp_v;
                    const pct = limit && v ? Math.min(100, (v / limit) * 100) : null;
                    return (
                      <tr key={s.id}>
                        <td>
                          <b>{s.id}</b>
                          <div className="small muted">{t("permit.equipmentStep.faces", { faces: s.faces.join(", ") })}</div>
                        </td>
                        <td className="small">{e.system.topology === "string" ? t("permit.equipmentStep.inputMppt", { inverter: s.inverter + 1, mppt: s.mppt + 1 }) : t("permit.equipmentStep.input", { inverter: s.inverter + 1 })}</td>
                        <td className="num">{f.num(s.modules)}</td>
                        <td>
                          <div className="pw-vbar" title={pct != null ? t("permit.equipmentStep.ofLimit", { pct: f.num(pct) }) : undefined}>
                            {pct != null && <span className={cx(pct > 100 ? "over" : pct > 92 && "tight")} style={{ width: `${pct}%` }} />}
                          </div>
                          <div className="small muted">
                            {s.voc_cold_v != null ? t("permit.equipmentStep.vocCold", { volts: f.num(s.voc_cold_v, 1) }) : `${f.num(s.vmp_v)} V`}
                            {s.vmp_hot_v != null ? ` · ${t("permit.equipmentStep.vmpHot", { volts: f.num(s.vmp_hot_v, 1) })}` : ""}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {e && (e.system.unassigned_modules ?? 0) > 0 && <p className="small" style={{ color: "var(--danger)" }}>{t("permit.equipmentStep.unassigned", { count: e.system.unassigned_modules ?? 0 })}</p>}
          </div>
        </Card>
        <DatasheetList ws={ws} />
      </div>
    </div>
  );
}
